import { normaliseRegistration } from '@/lib/parse';

import { EMPTY_REGISTER, parseRegister } from './parse';
import type { AircraftRegister, RegisterEntry } from './types';

/**
 * Loading the bundled register, once per page load, and only if something asks.
 *
 * The file is a few hundred kilobytes — small for a download, large for a
 * bundle — so it is a static asset fetched on demand rather than an import.
 * Nothing loads it until a pilot types a registration somewhere, and a pilot
 * who only drops KML files never pays for it at all. That is also why the
 * promise is cached rather than the data: two fields looking up at once must
 * share one request, not race for two.
 *
 * Every failure path lands on the same empty register. A build that has never
 * run the updater, a static host that 404s the asset, a device that is offline
 * — all of them mean "this app cannot tell you the hex code", and the form
 * says so and stays typeable. Nothing here throws at a caller.
 */

/** Where the updater writes, relative to whatever base path this build serves from. */
export const REGISTER_PATH = 'aircraft-register/nz-caa.json';

let cached: Promise<AircraftRegister> | null = null;

function registerUrl(): string {
  // `BASE_URL` ends in a slash in every Vite build, including the GitHub Pages
  // subpath build, which is the one where a leading-slash URL would 404.
  const base = import.meta.env.BASE_URL;
  return `${base.endsWith('/') ? base : `${base}/`}${REGISTER_PATH}`;
}

async function download(fetchImpl: typeof fetch): Promise<AircraftRegister> {
  try {
    const response = await fetchImpl(registerUrl());
    if (!response.ok) {
      return EMPTY_REGISTER;
    }
    return parseRegister(await response.json());
  } catch {
    // Offline, or a host serving `index.html` for a missing asset — either way
    // there is no register, which is a state the callers already render.
    return EMPTY_REGISTER;
  }
}

/**
 * The register, downloaded once and kept for the life of the page.
 *
 * @param fetchImpl  injected by tests; production passes nothing.
 */
export function loadRegister(fetchImpl: typeof fetch = fetch): Promise<AircraftRegister> {
  cached ??= download(fetchImpl);
  return cached;
}

/** Forget the cached download. Tests use this; the app has no reason to. */
export function resetRegisterCache(): void {
  cached = null;
}

/**
 * Look one registration up.
 *
 * @param raw  as typed — normalised here, so `zkdfh`, `ZK-DFH` and `zk dfh`
 *             all find the same aeroplane.
 * @returns the register's entry, or `undefined` if it has none — which covers
 *          both "no such aircraft" and "no register downloaded".
 */
export async function lookupRegistration(
  raw: string,
  fetchImpl: typeof fetch = fetch,
): Promise<RegisterEntry | undefined> {
  const registration = normaliseRegistration(raw);
  if (registration === '') {
    return undefined;
  }
  const register = await loadRegister(fetchImpl);
  return register.entries.get(registration);
}
